import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ClipboardList, Plus, Send, CheckCircle2, Download, Star, FileText, Trash2 } from "lucide-react";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";

import { useI18n } from "@/context/I18nContext";

const FIELD_TYPES = ["text", "number", "single", "multi", "rating", "date", "boolean", "photo"];

export default function Forms() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("form.manage");
  const [tab, setTab] = useState<string>("mine");
  const tabs = canManage
    ? [["mine", "My Forms"], ["templates", "Templates"], ["assignments", "Assignments"], ["responses", "Responses"]]
    : [["mine", "My Forms"]];
  return (
    <div data-testid="forms-page">
      <PageHeader title="Forms & Surveys" subtitle="Structured checklists and surveys, assigned to your scope" testId="forms-header" />
      {canManage && (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="forms-tabs">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`forms-tab-${key}`}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${tab === key ? "bg-brand text-white" : "bg-muted hover:bg-muted/70"}`}>{label}</button>
          ))}
        </div>
      )}
      {tab === "mine" && <MyForms />}
      {tab === "templates" && canManage && <Templates />}
      {tab === "assignments" && canManage && <Assignments />}
      {tab === "responses" && canManage && <Responses />}
    </div>
  );
}

// ------------------------- FRONTLINE: My Forms -------------------------
const MyForms: React.FC = () => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<any | null>(null);
  const load = useCallback(() => {
    api.get("/forms/assigned").then((r) => setRows(r.data.assigned || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <LoadingBlock />;
  if (open) return <FillForm item={open} onDone={() => { setOpen(null); load(); }} onCancel={() => setOpen(null)} />;
  return (
    (<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="myforms-list">
      {rows.length === 0 ? <Card className="p-6 sm:col-span-2 lg:col-span-3"><EmptyState icon={<ClipboardList size={24} />} title="No forms assigned" description="Forms assigned to your store or role will appear here." /></Card> :
        rows.map((f) => (
          <Card key={f.assignment_id} className="flex flex-col gap-2 p-4" data-testid={`myform-${f.assignment_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-medium">{f.kind === "survey" ? <FileText size={16} className="text-brand" /> : <ClipboardList size={16} className="text-brand" />}{f.template_name}</div>
              {f.submitted ? <Badge variant="secondary" className="gap-1" data-testid={`myform-done-${f.assignment_id}`}><CheckCircle2 size={12} />{" "}{t("Done")}</Badge> : <Badge className="bg-amber-500">{t("Due")}</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">{f.recurrence === "one_time" ? t("One-time") : f.recurrence}· {f.fields.length}{" "}{t("question")}{f.fields.length !== 1 ? "s" : ""}{f.due_date ? ` · ${tl("due")} ${f.due_date}` : ""}</div>
            <Button size="sm" variant={f.submitted ? "outline" : "default"} className="mt-1" onClick={() => setOpen(f)} data-testid={`myform-open-${f.assignment_id}`}>{f.submitted ? t("Review / update") : t("Fill out")}</Button>
          </Card>
        ))}
    </div>)
  );
};

const FillForm: React.FC<{ item: any; onDone: () => void; onCancel: () => void }> = ({ item, onDone, onCancel }) => {
  const {
    t
  } = useI18n();

  const [values, setValues] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(item.response_id || null);
  const set = (k: string, v: any) => setValues((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const answers = item.fields.filter((f: any) => f.type !== "photo").map((f: any) => ({ key: f.key, value: values[f.key] ?? null }));
      const r = await api.post("/forms/responses", { assignment_id: item.assignment_id, answers });
      setResponseId(r.data.response.id);
      toast.success("Response submitted");
      onDone();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Please complete required fields"));
    } finally { setBusy(false); }
  };
  const uploadPhoto = async (fieldKey: string, file: File) => {
    if (!responseId) { toast.error("Submit the form first, then attach photos"); return; }
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/forms/responses/${responseId}/photo?field_key=${encodeURIComponent(fieldKey)}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo attached"); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  return (
    (<Card className="mx-auto max-w-2xl p-5" data-testid="myform-fill">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-lg font-semibold">{item.template_name}</div>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="myform-cancel">{t("Back")}</Button>
      </div>
      <div className="space-y-4">
        {item.fields.map((f: any) => (
          <div key={f.key} data-testid={`field-${f.key}`}>
            <Label className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
            {f.type === "text" && <Textarea value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`input-${f.key}`} />}
            {f.type === "number" && <Input type="number" value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value === "" ? null : Number(e.target.value))} data-testid={`input-${f.key}`} />}
            {f.type === "date" && <Input type="date" value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`input-${f.key}`} />}
            {f.type === "boolean" && (
              <div className="flex gap-2">
                {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                  <button key={String(lbl)} type="button" onClick={() => set(f.key, val)} data-testid={`bool-${f.key}-${lbl}`}
                    className={`rounded-md border px-3 py-1.5 text-sm ${values[f.key] === val ? "border-brand bg-brand/10 text-brand" : "border-border"}`}>{lbl as string}</button>
                ))}
              </div>
            )}
            {f.type === "rating" && (
              <div className="flex gap-1" data-testid={`rating-${f.key}`}>
                {Array.from({ length: f.scale_max || 5 }).map((_, i) => (
                  <button key={i} type="button" onClick={() => set(f.key, i + 1)} data-testid={`rating-${f.key}-${i + 1}`}>
                    <Star size={22} className={(values[f.key] || 0) > i ? t("fill-amber-400 text-amber-400") : t("text-muted-foreground")} />
                  </button>
                ))}
              </div>
            )}
            {f.type === "single" && (
              <div className="flex flex-wrap gap-2">
                {f.options.map((o: string) => (
                  <button key={o} type="button" onClick={() => set(f.key, o)} data-testid={`opt-${f.key}-${o}`}
                    className={`rounded-md border px-3 py-1.5 text-sm ${values[f.key] === o ? "border-brand bg-brand/10 text-brand" : "border-border"}`}>{o}</button>
                ))}
              </div>
            )}
            {f.type === "multi" && (
              <div className="flex flex-wrap gap-2">
                {f.options.map((o: string) => {
                  const arr: string[] = values[f.key] || [];
                  const on = arr.includes(o);
                  return <button key={o} type="button" onClick={() => set(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])} data-testid={`opt-${f.key}-${o}`}
                    className={`rounded-md border px-3 py-1.5 text-sm ${on ? "border-brand bg-brand/10 text-brand" : "border-border"}`}>{o}</button>;
                })}
              </div>
            )}
            {f.type === "photo" && (
              <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadPhoto(f.key, e.target.files[0])} data-testid={`photo-${f.key}`} className="text-sm" />
            )}
          </div>
        ))}
      </div>
      <Button className="mt-5 w-full gap-2" onClick={submit} disabled={busy} data-testid="myform-submit"><Send size={16} />{" "}{t("Submit")}</Button>
    </Card>)
  );
};

// ------------------------- LEADERSHIP: Templates -------------------------
const Templates: React.FC = () => {
  const {
    t: tI18n
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => { api.get("/forms/templates").then((r) => setRows(r.data.templates || [])).catch(() => setRows([])).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id: string, action: string) => {
    try { await api.post(`/forms/templates/${id}/${action}`); toast.success(action === "publish" ? tl("Published") : tl("Archived")); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  if (creating) return <NewTemplate onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />;
  return (
    (<div>
      <div className="mb-3 flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setCreating(true)} data-testid="tpl-new"><Plus size={15} />{" "}{tI18n("New template")}</Button></div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.length === 0 ? <Card className="p-6 md:col-span-2"><EmptyState icon={<FileText size={24} />} title="No templates yet" /></Card> :
          rows.map((t) => (
            <Card key={t.id} className="flex flex-col gap-2 p-4" data-testid={`tpl-${t.id}`}>
              <div className="flex items-start justify-between">
                <div><div className="font-medium">{t.name}</div><div className="text-xs text-muted-foreground">{stat(t.category)}· {t.kind}· {t.fields.length}{" "}{tI18n("fields")}</div></div>
                <Badge variant={t.status === "published" ? "default" : t.status === "archived" ? "secondary" : "outline"} data-testid={`tpl-status-${t.id}`}>{stat(t.status)}{t.version ? ` v${t.version}` : ""}</Badge>
              </div>
              <div className="mt-1 flex gap-2">
                {t.status !== "archived" && <Button size="sm" variant="outline" onClick={() => act(t.id, "publish")} data-testid={`tpl-publish-${t.id}`}>{t.status === "published" ? tI18n("Re-publish") : tI18n("Publish")}</Button>}
                {t.status !== "archived" && <Button size="sm" variant="ghost" onClick={() => act(t.id, "archive")} data-testid={`tpl-archive-${t.id}`}>{tI18n("Archive")}</Button>}
              </div>
            </Card>
          ))}
      </div>
    </div>)
  );
};

const NewTemplate: React.FC<{ onDone: () => void; onCancel: () => void }> = ({ onDone, onCancel }) => {
  const {
    t: tI18n
  } = useI18n();

  const [name, setName] = useState("");const [kind, setKind] = useState("form");const [category, setCategory] = useState("Operations");
  const [fields, setFields] = useState<any[]>([{ key: "q1", label: "", type: "text", required: true, options: [] }]);
  const [busy, setBusy] = useState(false);
  const setF = (i: number, patch: any) => setFields((s) => s.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const create = async () => {
    setBusy(true);
    try {
      const payload = { name, kind, category, fields: fields.map((f) => ({ ...f, options: (typeof f.options === "string" ? f.options.split(",").map((x: string) => x.trim()).filter(Boolean) : f.options) })) };
      await api.post("/forms/templates", payload); toast.success("Draft created"); onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to create"); } finally { setBusy(false); }
  };
  return (
    (<Card className="mx-auto max-w-2xl p-5" data-testid="tpl-new-form">
      <div className="mb-4 flex items-center justify-between"><div className="text-lg font-semibold">{tI18n("New template")}</div><Button variant="ghost" size="sm" onClick={onCancel}>{tI18n("Back")}</Button></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label className="mb-1 block text-sm">{tI18n("Name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name" /></div>
        <div><Label className="mb-1 block text-sm">{tI18n("Category")}</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tpl-category" /></div>
        <div><Label className="mb-1 block text-sm">{tI18n("Kind")}</Label>
          <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="tpl-kind">
            <option value="form">{tI18n("Form / Checklist")}</option><option value="survey">{tI18n("Survey")}</option>
          </select>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <div className="text-sm font-medium">{tI18n("Questions")}</div>
        {fields.map((f, i) => (
          <div key={i} className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-[1fr_1fr_auto]" data-testid={`tpl-field-${i}`}>
            <Input placeholder={tI18n("Question label")} value={f.label} onChange={(e) => setF(i, { label: e.target.value })} data-testid={`tpl-field-label-${i}`} />
            <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={f.type} onChange={(e) => setF(i, { type: e.target.value })} data-testid={`tpl-field-type-${i}`}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={f.required} onChange={(e) => setF(i, { required: e.target.checked })} />{" "}{tI18n("req")}</label>
            {(f.type === "single" || f.type === "multi") && <Input className="sm:col-span-3" placeholder={tI18n("Options (comma separated)")} value={typeof f.options === "string" ? f.options : (f.options || []).join(", ")} onChange={(e) => setF(i, { options: e.target.value })} data-testid={`tpl-field-options-${i}`} />}
          </div>
        ))}
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setFields((s) => [...s, { key: `q${s.length + 1}`, label: "", type: "text", required: false, options: [] }])} data-testid="tpl-add-field"><Plus size={14} />{" "}{tI18n("Add question")}</Button>
      </div>
      <Button className="mt-5 w-full" onClick={create} disabled={busy || !name} data-testid="tpl-create">{tI18n("Create draft")}</Button>
    </Card>)
  );
};

// ------------------------- LEADERSHIP: Assignments -------------------------
const Assignments: React.FC = () => {
  const {
    t: tI18n
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>({ template_id: "", scope_id: "", recurrence: "one_time", roles: "" });
  const load = useCallback(() => {
    Promise.all([api.get("/forms/assignments"), api.get("/forms/templates"), api.get("/org/stores").catch(() => ({ data: { stores: [] } }))])
      .then(([a, t, s]) => { setRows(a.data.assignments || []); setTemplates((t.data.templates || []).filter((x: any) => x.status === "published")); setStores(s.data.stores || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.template_id || !form.scope_id) { toast.error("Pick a template and a store"); return; }
    try {
      await api.post("/forms/assignments", { template_id: form.template_id, scope_type: "store", scope_id: form.scope_id, recurrence: form.recurrence, roles: form.roles ? form.roles.split(",").map((x: string) => x.trim()) : [] });
      toast.success("Assigned"); setForm({ template_id: "", scope_id: "", recurrence: "one_time", roles: "" }); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to assign"); }
  };
  if (loading) return <LoadingBlock />;
  return (
    (<div className="grid gap-3 lg:grid-cols-[320px_1fr]">
      <Card className="space-y-3 p-4" data-testid="assign-new">
        <div className="text-sm font-medium">{tI18n("Assign a form")}</div>
        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="assign-template">
          <option value="">{tI18n("Select published template…")}</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })} data-testid="assign-store">
          <option value="">{tI18n("Select store…")}</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{tI18n("Store")}{" "}{s.store_number}</option>)}
        </select>
        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} data-testid="assign-recurrence">
          <option value="one_time">{tI18n("One-time")}</option><option value="daily">{tI18n("Daily")}</option><option value="weekly">{tI18n("Weekly")}</option>
        </select>
        <Input placeholder={tI18n("Roles (optional, comma separated)")} value={form.roles} onChange={(e) => setForm({ ...form, roles: e.target.value })} data-testid="assign-roles" />
        <Button className="w-full" onClick={create} data-testid="assign-create">{tI18n("Create assignment")}</Button>
      </Card>
      <div className="space-y-2">
        {rows.length === 0 ? <Card className="p-6"><EmptyState icon={<ClipboardList size={24} />} title="No assignments yet" /></Card> :
          rows.map((a) => (
            <Card key={a.id} className="flex items-center justify-between p-3.5" data-testid={`assign-${a.id}`}>
              <div><div className="font-medium">{a.template_name}</div><div className="text-xs text-muted-foreground">{a.scope_type} · {a.recurrence} · {a.status}{a.roles?.length ? ` · ${a.roles.join(", ")}` : ""}</div></div>
              <Badge variant="secondary" data-testid={`assign-count-${a.id}`}>{a.response_count}{" "}{tI18n("response")}{a.response_count !== 1 ? "s" : ""}</Badge>
            </Card>
          ))}
      </div>
    </div>)
  );
};

// ------------------------- LEADERSHIP: Responses -------------------------
const Responses: React.FC = () => {
  const {
    t
  } = useI18n();

  const [assignments, setAssignments] = useState<any[]>([]);
  const [sel, setSel] = useState<string>("");
  const [data, setData] = useState<any>(null);
  const [annot, setAnnot] = useState<string | null>(null);
  useEffect(() => { api.get("/forms/assignments").then((r) => { const a = r.data.assignments || []; setAssignments(a); if (a[0]) setSel(a[0].id); }).catch(() => {}); }, []);
  useEffect(() => { if (sel) api.get(`/forms/responses?assignment_id=${sel}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [sel]);
  const exportCsv = () => { window.open(`${(api.defaults.baseURL || "")}/forms/responses/export?assignment_id=${sel}`, "_blank"); };
  return (
    (<div data-testid="responses-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="resp-assignment">
          {assignments.map((a) => <option key={a.id} value={a.id}>{a.template_name} ({a.response_count})</option>)}
        </select>
        {sel && <Button size="sm" variant="outline" className="gap-1.5" onClick={exportCsv} data-testid="resp-export"><Download size={14} />{" "}{t("Export CSV")}</Button>}
      </div>
      {!data ? <Card className="p-6"><EmptyState icon={<FileText size={24} />} title="No data" /></Card> :
        <Card className="w-full overflow-x-auto p-0" data-testid="resp-table">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr><th className="p-2.5">{t("Respondent")}</th>{data.fields.map((f: any) => <th key={f.key} className="p-2.5">{f.label}</th>)}<th className="p-2.5">{t("Submitted")}</th></tr>
            </thead>
            <tbody>
              {data.responses.length === 0 ? <tr><td colSpan={data.fields.length + 2} className="p-6 text-center text-muted-foreground">{t("No responses yet")}</td></tr> :
                data.responses.map((r: any) => {
                  const amap: Record<string, any> = {}; (r.answers || []).forEach((a: any) => { amap[a.key] = a; });
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`resp-row-${r.id}`}>
                      <td className="p-2.5 font-medium">{r.user}</td>
                      {data.fields.map((f: any) => {
                        const a = amap[f.key] || {}; const v = a.value;
                        if (f.type === "photo") {
                          const ev = a.evidence || [];
                          return (
                            <td key={f.key} className="p-2.5">{ev.length ? (
                              <div className="flex flex-wrap gap-1.5">
                                {ev.map((p: any, idx: number) => (
                                  <button key={p.id} className="text-brand underline" onClick={() => setAnnot(p.id)} data-testid={idx === 0 ? `resp-photo-${r.id}` : `resp-photo-${r.id}-${idx}`}>{t("Photo")}{" "}{idx + 1}{" "}{t("· annotate")}</button>
                                ))}
                              </div>
                            ) : "—"}</td>
                          );
                        }
                        return <td key={f.key} className="p-2.5">{Array.isArray(v) ? v.join(", ") : v === true ? "Yes" : v === false ? "No" : (v ?? "—")}</td>;
                      })}
                      <td className="p-2.5 text-xs text-muted-foreground">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </Card>}
      {annot && <PhotoAnnotator attachmentId={annot} onClose={() => setAnnot(null)} />}
    </div>)
  );
};
